import { Box, Typography, Stack, debounce, StackProps } from '@mui/material';
import LoaderComponent from 'components/Loader';
import ReactECharts from 'echarts-for-react';
import { useEffect, useRef, useState } from 'react';
import { InfoTooltip } from './InfoTooltip';

type MultiLineWidgetProps = {
	color?: string;
	isLoading: boolean;
	title: any;
	data?: any | null;
	minTitleHeight?: `${number}px`;
	sectorLabel?: string;
	hasFilter?: boolean;
	children?: React.ReactNode;
	childrenContainerProps?: StackProps;
	/** Merged into the title + filter row Stack (e.g. force column layout on md to avoid overflow in narrow grid columns). */
	headerStackProps?: StackProps;
	containerColor?: string;
	titleColor?: string;
	hasTooltip?: boolean;
	tooltipLabel?: string | JSX.Element | { title: string; description: string | React.ReactNode }[];
};

const MultiLineWidget = ({
	color = '#1b1e35',
	isLoading = false,
	title = '',
	minTitleHeight = '38px',
	data = null,
	sectorLabel = '',
	hasFilter,
	childrenContainerProps,
	headerStackProps,
	containerColor,
	children,
	titleColor,
	hasTooltip = false,
	tooltipLabel = ''
}: Readonly<MultiLineWidgetProps>) => {
	const chartRef = useRef<any>(null);
	const [windowSize, setWindowSize] = useState(window.innerWidth);
	const defaultOptions = {
		backgroundColor: containerColor || '#003771',
	};

	useEffect(() => {
			// re-render chart to fix alignment when resizing the browser
			const handleResize = debounce(() => {
				if (chartRef.current) {
					setWindowSize(window.innerWidth);
				}
			}, 200);
	
			handleResize();
			window.addEventListener('resize', handleResize);
			return () => {
				window.removeEventListener('resize', handleResize);
			};
		}, []);

	return (
		<Box display="flex" flexGrow={1} height="100%" position="relative" sx={{ minWidth: 0, maxWidth: '100%' }}>
			<Stack minHeight="100px" p={2} bgcolor={containerColor || "primary.main"} fontFamily="Montserrat" borderRadius="5px" flexGrow={1} sx={{ minWidth: 0, maxWidth: '100%' }}>
				<Stack
					gap={2}
					direction={{ xs: 'column', sm: 'row' }}
					justifyContent="space-between"
					alignItems={{ sm: 'center' }}
					minHeight={minTitleHeight}
					{...headerStackProps}
				>
					<Stack direction="row" alignItems="center">
						<Typography variant="widgetTitle" color={titleColor}>{title}</Typography>
						{hasTooltip && !!tooltipLabel && <InfoTooltip label={tooltipLabel} />}
					</Stack>
					{hasFilter && (
						<Stack
							direction={{ xs: 'column', sm: 'row' }}
							gap={2}
							alignItems={{ xs: 'flex-end', md: 'center' }}
							justifyContent={{ xs: 'flex-end' }}
							width={{ xs: '100%', md: 'auto' }}
							pr={{ xs: 1, md: 'initial' }}
							my={{ xs: 2, md: 0 }}
							{...childrenContainerProps}
						>
							{children}
						</Stack>
					)}
				</Stack>
				{sectorLabel != '' && (
					<Stack
						sx={{
							display: 'flow-root !important'
						}}
					>
						<Box
							bgcolor="backgroundLight.primary"
							sx={{
								borderRadius: '16px',
								width: 'auto',
								float: 'right',
								display: 'flex',
								marginTop: '-36px',
								height: 'auto',
								alignItems: 'center',
								padding: '5px'
							}}
						>
							<Typography
								variant="bodyText"
								sx={{ textAlign: 'center' }}
							>
								{sectorLabel}
							</Typography>
						</Box>
					</Stack>
				)}
				<Box flexGrow={1} sx={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
					{!isLoading ? (
						<ReactECharts
							key={windowSize}
							ref={chartRef}
							option={data && Object.keys(data) ? data : defaultOptions}
							theme={{
								backgroundColor: { color }
							}}
						/>
					) : (
						<LoaderComponent pt={10}/>
					)}
				</Box>
			</Stack>
		</Box>
	);
};

export default MultiLineWidget;
